import businessHero from '@/assets/marketing/images/insurance-verticals/business-hero.jpg'
import businessShare from '@/assets/marketing/images/insurance-verticals/business-share.jpg'
import constructionCoverages from '@/assets/marketing/images/insurance-verticals/construction-coverages.jpg'
import constructionHero from '@/assets/marketing/images/insurance-verticals/construction-hero.jpg'
import constructionShare from '@/assets/marketing/images/insurance-verticals/construction-share.jpg'
import electricalCoverages from '@/assets/marketing/images/insurance-verticals/electrical-coverages.jpg'
import electricalHero from '@/assets/marketing/images/insurance-verticals/electrical-hero.jpg'
import electricalShare from '@/assets/marketing/images/insurance-verticals/electrical-share.jpg'
import hvacCoverages from '@/assets/marketing/images/insurance-verticals/hvac-coverages.jpg'
import hvacHero from '@/assets/marketing/images/insurance-verticals/hvac-hero.jpg'
import hvacShare from '@/assets/marketing/images/insurance-verticals/hvac-share.jpg'
import landscapingCoverages from '@/assets/marketing/images/insurance-verticals/landscaping-coverages.jpg'
import landscapingHero from '@/assets/marketing/images/insurance-verticals/landscaping-hero.jpg'
import landscapingShare from '@/assets/marketing/images/insurance-verticals/landscaping-share.jpg'
import plumbingCoverages from '@/assets/marketing/images/insurance-verticals/plumbing-coverages.jpg'
import plumbingHero from '@/assets/marketing/images/insurance-verticals/plumbing-hero.jpg'
import plumbingShare from '@/assets/marketing/images/insurance-verticals/plumbing-share.jpg'
import type { InsuranceVerticalSlug } from '@/config/insuranceVerticals'

export interface VerticalCoverage {
  name: string
  description: string
}

/** A kind of customer the trade page speaks to, shown in the "Who it's for" cards. */
export interface VerticalAudience {
  label: string
  title: string
}

export interface VerticalFeature {
  title: string
  body: string
}

/** The sample certificate-holder email thread animated in the COI section. */
export interface VerticalCoiRequest {
  requesterName: string
  requesterEmail: string
  certificateHolder: string
  businessName: string
}

export interface InsuranceVertical {
  slug: InsuranceVerticalSlug
  /** This trade's HubSpot quote-request form, so leads arrive tagged by trade. */
  hubspotFormId: string
  /** Lowercase trade name used mid-sentence, e.g. "plumbing insurance". */
  trade: string
  /** Shown after "Cai | " in the browser tab and as the link-preview title. */
  pageTitle: string
  metaDescription: string
  /** 1200×630 crop of the hero for link previews. */
  shareImage: string
  heroSub: string
  heroImage: string
  heroImageAlt: string
  audienceHeading: string
  audiences: VerticalAudience[]
  coverages: VerticalCoverage[]
  coveragesImage: string
  coveragesImageAlt: string
  coiSetup: string
  coiBody: string
  coiRequest: VerticalCoiRequest
  features: VerticalFeature[]
}

const BUSINESS_OWNERS_POLICY: VerticalCoverage = {
  name: 'Business Owner’s Policy',
  description:
    'Bundles General Liability with coverage for your shop, office, and equipment on site, in one policy',
}

function cyber(who: string, system: string): VerticalCoverage {
  return { name: 'Cyber', description: `Covers you if ${who} payment info or your ${system} is hacked` }
}

function features(addSomething: string): VerticalFeature[] {
  return [
    {
      title: 'Competitive quotes, fast',
      body: 'We work with top insurance carriers to find the best quotes for your business.',
    },
    {
      title: 'Easy price comparisons',
      body: 'Cai’s AI lines up your quotes side by side, so you can see what you get for the price.',
    },
    {
      title: 'Seamless policy management',
      body: `We make it easy to get a COI, ${addSomething}, or update your policy.`,
    },
  ]
}

export const INSURANCE_VERTICALS: Record<InsuranceVerticalSlug, InsuranceVertical> = {
  hvac: {
    slug: 'hvac',
    hubspotFormId: '91f606ae-ee29-4924-9fb3-c83fff69a7ae',
    trade: 'HVAC',
    pageTitle: 'Insurance for HVAC companies',
    shareImage: hvacShare,
    metaDescription:
      'Insurance that doesn’t hold your HVAC business back. Coverage for your vehicles, crew, and tools, plus AI that makes policy management easy.',
    heroSub: 'Coverage for your vans, crew, and every job, from service calls to commercial installs.',
    heroImage: hvacHero,
    heroImageAlt: 'An HVAC tech in a white service van, with more vans parked outside the shop',
    audienceHeading: 'Built for home services and subcontractors',
    audiences: [
      { label: 'Home services', title: 'Service calls, repairs, and replacements in customers’ homes' },
      { label: 'Subcontractors', title: 'New construction and commercial installs for GCs and builders' },
    ],
    coverages: [
      {
        name: 'Commercial Auto Liability',
        description: 'Covers accidents in your work vehicles, including the ones your techs drive home',
      },
      {
        name: 'General Liability',
        description:
          "Covers damage or injury at a customer's home or business, like a water leak after an install",
      },
      BUSINESS_OWNERS_POLICY,
      {
        name: 'Workers Compensation',
        description: 'Covers your techs if they get hurt on the job',
      },
      {
        name: 'Physical Damage',
        description: 'Covers repairing or replacing your vans after a crash, storm, or theft',
      },
      {
        name: 'Tools and Equipment',
        description: 'Covers your tools and equipment if they’re stolen or damaged',
      },
      cyber('customer', 'booking system'),
    ],
    coveragesImage: hvacCoverages,
    coveragesImageAlt: 'A line of white service vans with ladder racks parked outside a shop',
    coiSetup: 'Ever lost a job to a slow COI?',
    coiBody: 'Send COIs to property managers and commercial clients without waiting on a call back.',
    coiRequest: {
      requesterName: 'Mike Torres',
      requesterEmail: 'mike@summitair.com',
      certificateHolder: 'Lakeview Property Management',
      businessName: 'Summit Heating & Air LLC',
    },
    features: features('add a van'),
  },
  plumbing: {
    slug: 'plumbing',
    hubspotFormId: '7885df4c-c14f-4511-8773-f9c1c70c5b8c',
    trade: 'plumbing',
    pageTitle: 'Insurance for plumbers',
    shareImage: plumbingShare,
    metaDescription:
      'Insurance that doesn’t hold your plumbing business back. Coverage for your vehicles, crew, and tools, plus AI that makes policy management easy.',
    heroSub: 'Coverage for your vans, tools, and crew, from service calls to new construction.',
    heroImage: plumbingHero,
    heroImageAlt: 'Two plumbers carrying tool bags to a customer’s door',
    audienceHeading: 'Built for home services and subcontractors',
    audiences: [
      { label: 'Home services', title: 'Service calls, repairs, and drain work in customers’ homes' },
      { label: 'Subcontractors', title: 'Rough-in and commercial work for builders and GCs' },
    ],
    coverages: [
      { name: 'Commercial Auto Liability', description: 'Covers accidents in your work vehicles' },
      {
        name: 'General Liability',
        description:
          'Covers damage or injury tied to your work, like water damage after a repair in a home or on a job site',
      },
      BUSINESS_OWNERS_POLICY,
      {
        name: 'Workers Compensation',
        description: 'Covers your crew if they get hurt on the job',
      },
      {
        name: 'Physical Damage',
        description: 'Covers repairing or replacing your vans after a crash, storm, or theft',
      },
      {
        name: 'Tools and Equipment',
        description: 'Covers your tools and equipment if they’re stolen or damaged',
      },
      cyber('customer', 'booking system'),
    ],
    coveragesImage: plumbingCoverages,
    coveragesImageAlt: 'White plumbing vans with pipe racks parked outside a shop',
    coiSetup: 'Ever had a COI hold up a job?',
    coiBody: 'Send COIs to builders and property managers without waiting on a call back.',
    coiRequest: {
      requesterName: 'Jen Walsh',
      requesterEmail: 'jen@clearflowplumbing.com',
      certificateHolder: 'Harbor Homes Builders',
      businessName: 'Clearflow Plumbing LLC',
    },
    features: features('add a van'),
  },
  electrical: {
    slug: 'electrical',
    hubspotFormId: 'da68670e-14f8-4b6f-92eb-ef6c7b36ed7a',
    trade: 'electrical',
    pageTitle: 'Insurance for electricians',
    shareImage: electricalShare,
    metaDescription:
      'Insurance that doesn’t hold your electrical business back. Coverage for your vehicles, crew, and tools, plus AI that makes policy management easy.',
    heroSub: 'Coverage for residential service or commercial jobs, with instant COIs when a GC needs one.',
    heroImage: electricalHero,
    heroImageAlt: 'Three electricians in hard hats walking into an electrical room',
    audienceHeading: 'Built for home services and subcontractors',
    audiences: [
      { label: 'Home services', title: 'Repairs, panel upgrades, and service calls in customers’ homes' },
      { label: 'Subcontractors', title: 'Commercial and new construction work for GCs' },
    ],
    coverages: [
      { name: 'Commercial Auto Liability', description: 'Covers accidents in your work vehicles' },
      {
        name: 'General Liability',
        description:
          'Covers damage or injury tied to your work, like a fire after a panel upgrade in a home or a commercial build',
      },
      BUSINESS_OWNERS_POLICY,
      {
        name: 'Workers Compensation',
        description: 'Covers your electricians if they get hurt on the job',
      },
      {
        name: 'Physical Damage',
        description: 'Covers repairing or replacing your vans and trucks after a crash, storm, or theft',
      },
      {
        name: 'Tools and Equipment',
        description: 'Covers your tools and equipment if they’re stolen or damaged',
      },
      cyber('customer', 'booking system'),
    ],
    coveragesImage: electricalCoverages,
    coveragesImageAlt: 'Electrical service trucks and a van with ladder racks in a parking lot',
    coiSetup: 'Ever had a slow COI hold up a job?',
    coiBody: 'Send COIs to general contractors and commercial clients without waiting on a call back.',
    coiRequest: {
      requesterName: 'Chris Park',
      requesterEmail: 'chris@brightlineelectric.com',
      certificateHolder: 'Keystone General Contractors',
      businessName: 'Brightline Electric LLC',
    },
    features: features('add a van'),
  },
  landscaping: {
    slug: 'landscaping',
    hubspotFormId: 'bbeb5cfa-309e-455a-8e00-c2836cc84b3d',
    trade: 'landscaping',
    pageTitle: 'Insurance for landscapers',
    shareImage: landscapingShare,
    metaDescription:
      'Insurance that doesn’t hold your landscaping business back. Coverage for your vehicles, crew, and tools, plus AI that makes policy management easy.',
    heroSub: 'Trucks, trailers, and seasonal crews, covered for homeowners, HOAs, and commercial properties.',
    heroImage: landscapingHero,
    heroImageAlt: 'A landscaper on a ride-on mower, with another trimming the edge of a commercial lawn',
    audienceHeading: 'Built for residential and commercial work',
    audiences: [
      { label: 'Residential', title: 'Homeowners and HOA neighborhoods' },
      { label: 'Commercial', title: 'Office parks, retail centers, and commercial properties' },
    ],
    coverages: [
      {
        name: 'Commercial Auto Liability',
        description: 'Covers accidents in your work vehicles, including while towing trailers',
      },
      {
        name: 'General Liability',
        description:
          "Covers damage to a homeowner's or client's property, like a rock thrown through a window by a mower",
      },
      BUSINESS_OWNERS_POLICY,
      {
        name: 'Workers Compensation',
        description: 'Covers your crew if they get hurt on the job',
      },
      {
        name: 'Physical Damage',
        description: 'Covers repairing or replacing your trucks and trailers after a crash, storm, or theft',
      },
      {
        name: 'Tools and Equipment',
        description: 'Covers your tools and equipment if they’re stolen or damaged',
      },
      cyber('client', 'scheduling system'),
    ],
    coveragesImage: landscapingCoverages,
    coveragesImageAlt: 'A pickup towing a trailer loaded with a ride-on mower',
    coiSetup: 'Ever lost a job to a slow COI?',
    coiBody: 'Send COIs to HOAs and commercial properties without waiting on a call back.',
    coiRequest: {
      requesterName: 'Luis Ortega',
      requesterEmail: 'luis@greenridgelawn.com',
      certificateHolder: 'Oak Hollow HOA',
      businessName: 'Greenridge Lawn & Landscape LLC',
    },
    features: features('add a trailer'),
  },
  construction: {
    slug: 'construction',
    hubspotFormId: 'f37d1dba-76ca-4536-b001-5d634e071f70',
    trade: 'construction',
    pageTitle: 'Insurance for construction companies',
    shareImage: constructionShare,
    metaDescription:
      'Insurance that doesn’t hold your construction business back. Coverage for your vehicles, crew, and tools, plus AI that makes policy management easy.',
    heroSub: 'Your job is building. Ours is finding you the right coverage for a strong foundation.',
    heroImage: constructionHero,
    heroImageAlt: 'A utility truck loaded with lumber at a building site, with a worker in a hard hat nearby',
    audienceHeading: 'Built for general contractors and subcontractors',
    audiences: [
      { label: 'General contractors', title: 'Running the job site and hiring subs' },
      { label: 'Subcontractors', title: 'Working on a GC’s job site, with the COIs and limits to match' },
    ],
    coverages: [
      {
        name: 'Commercial Auto Liability',
        description: 'Covers accidents in your work vehicles',
      },
      {
        name: 'General Liability',
        description: "Covers damage or injury on the job site, and it's often required to win a bid",
      },
      BUSINESS_OWNERS_POLICY,
      {
        name: 'Workers Compensation',
        description: 'Covers your crew if they get hurt on the job',
      },
      {
        name: 'Physical Damage',
        description: 'Covers repairing or replacing your trucks after a crash, storm, or theft',
      },
      {
        name: 'Tools and Equipment',
        description: 'Covers your tools and equipment if they’re stolen or damaged',
      },
      { name: 'Cyber', description: 'Covers you if client payment info or your project files are hacked' },
    ],
    coveragesImage: constructionCoverages,
    coveragesImageAlt: 'Work trucks and a dump truck lined up at a construction supply yard',
    coiSetup: 'Ever lost a bid to a slow COI?',
    coiBody: 'Send COIs to general contractors and cities without waiting on a call back.',
    coiRequest: {
      requesterName: 'Sam Reed',
      requesterEmail: 'sam@reedsitework.com',
      certificateHolder: 'City of Fairview',
      businessName: 'Reed Sitework LLC',
    },
    features: features('add a truck'),
  },
  business: {
    slug: 'business',
    hubspotFormId: '536fc8ac-3d27-4d1b-b4dd-f0ad503751b0',
    trade: 'business',
    pageTitle: 'Business insurance',
    shareImage: businessShare,
    metaDescription:
      'Insurance that doesn’t hold your business back. Coverage for your vehicles, crew, and tools, plus AI that makes policy management easy.',
    heroSub: 'Coverage for your vehicles, crew, and every job, from one insurance broker.',
    heroImage: businessHero,
    heroImageAlt: 'Three workers carrying tools and a hard hat past a white utility truck into a warehouse',
    audienceHeading: 'Built for the businesses that build, move, and fix the world around us',
    audiences: [
      { label: 'Build', title: 'Contractors, subcontractors, and builders working on job sites' },
      { label: 'Move', title: 'Trucking, delivery, and businesses running a fleet' },
      { label: 'Fix', title: 'Home services, repair, and maintenance in customers’ homes and businesses' },
      { label: 'Make', title: 'Manufacturers, shops, and warehouses' },
    ],
    coverages: [
      {
        name: 'Commercial Auto Liability',
        description: 'Covers accidents in your work vans, trucks, and fleet vehicles',
      },
      {
        name: 'General Liability',
        description: 'Covers damage or injury tied to your work, at a customer’s property or on a job site',
      },
      BUSINESS_OWNERS_POLICY,
      {
        name: 'Workers Compensation',
        description: 'Covers your crew if they get hurt on the job',
      },
      {
        name: 'Physical Damage',
        description: 'Covers repairs to your vehicles after a crash, theft, or storm',
      },
      {
        name: 'Tools and Equipment',
        description: 'Covers tools and equipment on the job, in storage, or in a vehicle',
      },
      {
        name: 'Cyber',
        description: 'Covers you if customer payment info or your business systems are hacked',
      },
    ],
    coveragesImage: hvacCoverages,
    coveragesImageAlt: 'A line of white service vans with ladder racks parked outside a shop',
    coiSetup: 'Ever had a slow COI hold up a job?',
    coiBody: 'Send COIs to clients, contractors, and property managers without waiting on a call back.',
    coiRequest: {
      requesterName: 'Dana Brooks',
      requesterEmail: 'dana@brooksfieldservices.com',
      certificateHolder: 'Northgate Property Group',
      businessName: 'Brooks Field Services LLC',
    },
    features: features('add a vehicle'),
  },
}
